import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Button,
  Heading,
  Stack,
  Flex,
} from "@chakra-ui/react";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { tableStyles } from "../../../utils/styles/tableStyles";
import Pagination from "../../../components/organism/Pagination/Pagination";
import useTableRendering from "../../../hooks/table/useTableRendering";
import { TableConfig } from "../../../types/Components/Table/TableConfig";
import LineGraph from "../../../components/molecule/Graph/LineGraph";
import { downloadPageToPDF } from "../../../helpers/downloadPageToPDF";
import { ModalConfig } from "../../../types/Components/Modal/ModalConfig";
import { getIndicatorRegionHistory } from "../../../services/Indicator/getIndicatorRegionHistory";
import TableIndicatorHistory from "../TableIndicatorHistory";
import { IndicatorHistory } from "../../../types/Indicator/IndicatorDataReport/IndicatorHistory";
import IndicatorDetails from "./IndicatorDetails";
import { PaginationValues } from "../../../types/General/PaginationValues";
import { useFilter } from "../../../hooks/filter/useFilter";
import { useForm } from "react-hook-form";
import { Option } from "../../../types/General/Option";
import { Dayjs } from "dayjs";
import useFetchData from "../../../hooks/data/useFetchData";
import { getDataPeriod } from "../../../services/Indicator/getDataPeriod";
import { historyGraphSelectFilterData } from "../data/historyGraphSelectFilterData";
import { graphStyle } from "../../../utils/styles/Graph/graphStyle";
import { PAGE_SIZE_OPTIONS } from "../../../utils/constants";
import { getFiltersArray } from "../../../helpers/getFiltersArray";
import { Auxiliares } from "../../../types/TabelasAuxiliares/Auxiliares";
import { Indicator } from "../../../types/Indicator/Indicator";
import {
  renderEmptyState,
  renderLoadingState,
} from "../../../hooks/table/TableStates";
import { borderRadius, borderStyle } from "../../../utils/styles/styles";

interface FilterState {
  StartPeriod: Option | null;
  EndPeriod: Option | null;
  StartYear: Dayjs | null;
  EndYear: Dayjs | null;
}

interface OptionsState {
  startPeriodOptions: Option[];
  endPeriodOptions: Option[];
  startYearOptions: Dayjs[];
  endYearOptions: Dayjs[];
}

interface ModalIndicatorProps {
  control: any;
  modalConfig: ModalConfig;
  indicatorId: number | undefined;
  provinceId: number | undefined;
  frequency: Auxiliares | null;
  countyId?: number | undefined;
  historyTitle?: string;
}

const ModalIndicator: FC<ModalIndicatorProps> = ({
  control,
  modalConfig,
  indicatorId,
  frequency,
  provinceId,
  countyId,
  historyTitle,
}) => {
  const { isOpen, title = "", buttonTitle, closeModal } = modalConfig;

  const [pageSizeOptions] = useState(PAGE_SIZE_OPTIONS);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
  const [isHistoryLoaded, setIsHistoryLoaded] = useState<boolean>(true);
  const modalRef = useRef<HTMLDivElement>(null);
  const [historyData, setHistoryData] = useState<IndicatorHistory[]>([]);
  const [historyDataGraph, setHistoryDataGraph] = useState<IndicatorHistory[]>(
    [],
  );
  const [indicator, setIndicator] = useState<Indicator>();
  const [errorFindingData, setErrorFindingData] = useState(false);
  const [isSearching, setIsSearching] = useState<boolean>(true);
  const [filterState, setFilterState] = useState<FilterState>({
    StartPeriod: null,
    EndPeriod: null,
    StartYear: null,
    EndYear: null,
  });
  const [totalItems, setTotalItems] = useState<number>(0);

  const [optionsState, setOptionsState] = useState<OptionsState>({
    startPeriodOptions: [] as Option[],
    endPeriodOptions: [] as Option[],
    startYearOptions: [] as Dayjs[],
    endYearOptions: [] as Dayjs[],
  });

  const { setValue } = useForm();

  const filterNames = useMemo(
    () => ["StartPeriod", "EndPeriod", "StartYear", "EndYear"],
    [],
  );
  const { filters, setFilter } = useFilter({ setValue, filterNames });

  const filtersArray = getFiltersArray(filterNames, filters, setFilter);

  const resetFilters = () => {
    setFilterState({
      StartPeriod: null,
      EndPeriod: null,
      StartYear: null,
      EndYear: null,
    });
  };

  useEffect(() => {
    if (!isOpen) {
      resetFilters();
      setIsHistoryLoaded(false);
      setIsSearching(true);
      setTotalItems(0);
      filtersArray.forEach((filter) => {
        filter.setValue("");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const fetchData = (
      filterValues?: any,
      paginationValues?: PaginationValues,
    ) => {
      setErrorFindingData(false);
      const params = {
        IndicatorId: indicatorId,
        FrequencyId: frequency?.id,
        ProvinceId: provinceId ?? "",
        CountyId: countyId ?? "",
      };
      getIndicatorRegionHistory(params, undefined, paginationValues)
        .then((result) => {
          setHistoryData(result.content);
          setIsHistoryLoaded(true);
          setIsSearching(false);
          setTotalItems(result.totalItems);
          setIndicator(result.indicator);
        })
        .catch((error) => {
          console.log("API Error:", error);
          setErrorFindingData(true);
          setIsSearching(false);
        });
      getIndicatorRegionHistory(params, filterValues)
        .then((result) => {
          setHistoryDataGraph(result.content);
        })
        .catch((error) => {
          console.log("API Error:", error);
        });
    };
    indicatorId &&
      provinceId &&
      fetchData(filters, {
        CurrentPage: currentPage,
        PageSize: pageSize,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicatorId, provinceId, isSearching]);

  const getDataPeriodCallback = useCallback(() => {
    return getDataPeriod(Number(frequency?.id));
  }, [frequency]);

  const { isLoaded: periodLoaded } = useFetchData(
    getDataPeriodCallback,
    (data) => {
      setOptionsState((prev) => ({
        ...prev,
        startPeriodOptions: data,
        endPeriodOptions: data,
      }));
    },
  );

  const filterChildren = historyGraphSelectFilterData(
    optionsState.startPeriodOptions,
    optionsState.endPeriodOptions,
    control,
    setFilter,
    (StartYear: any) => setFilterState((prev) => ({ ...prev, StartYear })),
    (EndYear: any) => setFilterState((prev) => ({ ...prev, EndYear })),
    (StartPeriod: any) => setFilterState((prev) => ({ ...prev, StartPeriod })),
    (EndPeriod: any) => setFilterState((prev) => ({ ...prev, EndPeriod })),
    filterState.StartYear,
    filterState.EndYear,
    periodLoaded,
  );

  const modalTableConfig: TableConfig = {
    tableName: `Histórico do indicador (Província ${historyTitle})`,
    dataTable: historyData,
    setDataTable: setHistoryData,
    renderTable: (historyData) => (
      <TableIndicatorHistory
        dataTable={historyData}
        isLoaded={isHistoryLoaded}
        frequencyId={frequency?.id ?? 0}
      />
    ),
    isLoaded: isHistoryLoaded,
    errorFindingData: errorFindingData,
  };

  const { currentPage, totalPages, handlePageChange, renderContent } =
    useTableRendering(modalTableConfig, pageSize, totalItems, setIsSearching);

  const handleDownloadPDF = () => {
    if (modalRef.current) {
      downloadPageToPDF(modalRef.current, "indicator.pdf");
    }
  };

  useEffect(() => {
    if (
      (filterState.StartPeriod && filterState.StartYear) ||
      (filterState.EndPeriod && filterState.EndYear)
    ) {
      setIsSearching(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const getGraphTitle = () => {
    const startPeriodTitle =
      filterState.StartPeriod && filterState.StartYear
        ? `${filterState.StartPeriod.name}/${filterState.StartYear.year()}`
        : "";
    const endPeriodTitle =
      filterState.EndPeriod && filterState.EndYear
        ? `${filterState.EndPeriod.name}/${filterState.EndYear.year()}`
        : "";

    if (startPeriodTitle && endPeriodTitle) {
      return `${startPeriodTitle} - ${endPeriodTitle}`;
    } else if (startPeriodTitle) {
      return `${startPeriodTitle} - Fim`;
    } else if (endPeriodTitle) {
      return `Início - ${endPeriodTitle}`;
    }

    return "";
  };

  const renderTableSection = () => {
    return (
      <Stack gap="4">
        <Flex
          direction="row"
          gap="4"
          justifyContent="space-between"
          alignItems="center"
        >
          <Heading as="h4" fontSize="xl">
            {modalTableConfig.tableName}
          </Heading>
          <Pagination
            totalResults={totalItems}
            pageSizeOptions={pageSizeOptions}
            totalPages={totalPages}
            currentPage={currentPage}
            pageSize={pageSize}
            handlePageChange={handlePageChange}
            setPageSize={setPageSize}
          />
        </Flex>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          sx={tableStyles}
        >
          {renderContent()}
        </Box>
      </Stack>
    );
  };

  const renderGraphSection = () => {
    return (
      <Box>
        <Flex direction="column" gap="4">
          <Heading as="h4" fontSize="xl">
            Gráfico
          </Heading>
          {filterChildren}
        </Flex>

        <Box sx={graphStyle}>
          <Flex direction="column" gap="4">
            <Heading as="h4" fontSize="xl">
              {getGraphTitle()}
            </Heading>
            <LineGraph
              data={historyDataGraph}
              width="100%"
              height={300}
              lineName={frequency?.name ?? ""}
            />
          </Flex>
        </Box>
      </Box>
    );
  };

  const renderDataContent = (historyData: any[]) => {
    if (!isHistoryLoaded)
      return (
        <Box border={borderStyle} borderBottomRadius={borderRadius} padding="3">
          {renderLoadingState()}
        </Box>
      );
    return (
      <>
        {historyData.length > 1 && renderTableSection()}
        {historyData.length > 1 && renderGraphSection()}
        {historyData.length <= 1 && (
          <Box
            border={borderStyle}
            borderBottomRadius={borderRadius}
            padding="3"
          >
            {renderEmptyState("Sem histórico de indicadores")}
          </Box>
        )}
      </>
    );
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Modal isOpen={isOpen} onClose={closeModal} isCentered>
        <ModalOverlay />
        <ModalContent
          ref={modalRef}
          boxShadow="none"
          border="none"
          overflow="hidden"
          maxW="80vw"
          maxH="90vw"
        >
          <ModalHeader fontSize="xl" fontWeight="bold">
            {title}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="4">
            <Stack gap="8">
              <IndicatorDetails
                indicator={indicator ?? control._formValues.row?.indicator}
              />
              {renderDataContent(historyData)}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="space-between" width="100%">
              <Button onClick={handleDownloadPDF} variant="secondary">
                Baixar o PDF
              </Button>
              <Button onClick={closeModal}>{buttonTitle}</Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </form>
  );
};

export default ModalIndicator;
